/**
 * This module requires the following DOM elements to be present on current page:
 *
 * - #mx-locations-sidebar
 * - #mx-restaurants-list
 * - #restaurant-filters
 * - #signature-locations-target
 *
 * See [`restaurants.php`](../../../www/restaurants.php) to see the expected HTML structure.
 */
import { batch, effect, signal } from "@preact/signals-core";
import MoxiesSelect from "src/components/select/select";

/** The selected region, e.g. Texas or Alberta */
const region = signal("");
/** The selected country, Canada or USA */
const country = signal("");

/** @type Map<string, HTMLDivElement[]> */
const byRegion = new Map();
/** @type Map<string, HTMLDivElement[]> */
const byCountry = new Map();
/** @type Map<string, string[]> */
const countryRegions = new Map();

// Elements in Wordpress page required
const sidebarEl = document.getElementById("mx-locations-sidebar");
const listEl = document.getElementById("mx-restaurants-list");

// Interactive elements
const formEl = /** @type HTMLFormElement */ (
  document.getElementById("restaurant-filters")
);
const countryEl = /** @type MoxiesSelect */ (
  document.querySelector("#country")
);
const regionEl = /** @type MoxiesSelect */ (document.querySelector("#region"));

/**
 * Clones then replaces an elementor loop grid with a filtered subset
 * @param {HTMLDivElement[]} elements
 */
function updateList(elements) {
  const restaurantEls = listEl.querySelectorAll(".type-restaurants");
  const gridContainer = listEl.querySelector(".elementor-loop-container");
  restaurantEls.forEach((el) => {
    gridContainer.removeChild(el);
  });
  elements.forEach((el) => {
    gridContainer.appendChild(el);
  });
}

/**
 * Update the filter state when the form changes
 * @param {Event} event
 */
function handleFormChange(event) {
  event.preventDefault();
  const nextCountry = document.querySelector("#country").getAttribute("value");
  let nextRegion = document.querySelector("#region").getAttribute("value");

  if (country.value !== nextCountry) {
    nextRegion = "";
  }

  country.value = nextCountry;
  region.value = nextRegion;
}

/**
 * Handles left-nav clicks
 * @param {Event} event
 */
function handleListClick(event) {
  const target = /** @type HTMLAnchorElement */ (event.target);

  if (target.localName === "a") {
    event.preventDefault();
    const value = target.textContent.trim();
    const countryValue = target
      .closest("details")
      .querySelector("summary")
      ?.textContent.trim();
    batch(() => {
      region.value = value;
      if (countryValue) {
        country.value = countryValue;
      }
    });
  }
}


function main() {
  /**
   * # Setup
   *
   * Traverses the DOM as the "database" for populating the options. This is done
   * so Elementor can still be leveraged for the layout and new regions/countries can
   * be added without the need to update the JS.
   */
  sidebarEl.querySelectorAll("summary").forEach((el) => {
    const countryName = el.textContent.trim();
    const regions = [];
    byCountry.set(countryName, []);
    countryRegions.set(countryName, regions);
    el.closest("details")
      .querySelectorAll("a")
      .forEach((el) => {
        const regionName = el.textContent.trim();
        byRegion.set(regionName, []);
        regions.push(regionName);
      });
  });

  /**
   * Cache all the list nodes and recycle them as needed. Each restaurant
   * should have a `div` with the following attributes on it:
   *
   * - `data-country`: Should be something like `Canada` or `USA`
   * - `data-region`: The region can be a word like `British Columbia`.
   *
   * These values will populate 2 maps, a `byRegion` and `byCountry` with a
   * nested array of all the nodes for each cached there.
   */
  const restaurantEls = /** @type NodeListOf<HTMLDivElement> */ (
    listEl.querySelectorAll(".type-restaurants")
  );

  restaurantEls.forEach((el) => {
    const dataEl = el.querySelector("[data-restaurant-item]");
    const country = dataEl.getAttribute("data-country");
    const region = dataEl.getAttribute("data-region");

    if (byRegion.has(region)) {
      byRegion.get(region).push(el);
    }

    if (byCountry.has(country)) {
      byCountry.get(country).push(el);
    }
  });

  // Populate the countries, they need to be ready at render.
  const countryOptions = [];
  for (const key of byCountry.keys()) {
    countryOptions.push({
      label: key,
      value: key,
    });
  }
  countryEl.options = countryOptions;

  country.subscribe((c) => {
    if (c) {
      updateList(byCountry.get(c));
    }
  });

  region.subscribe((r) => {
    const titleEl = document.getElementById("restaurants-locations-title");

    if (r) {
      updateList(byRegion.get(region.value));
      titleEl
        .querySelector(".moxies-title")
        .childNodes.forEach((node, index) => {
          if (index === 0) {
            if (node.nodeType === node.TEXT_NODE) {
              node.textContent = `${r} `;
            }
          }
        });
    }

    regionEl.setAttribute("value", r);
  });

  // React to the country/region changes and update the `mx-select` region options
  effect(() => {
    const signatureLocationsEl = document.getElementById("signature-locations");
    if (signatureLocationsEl) {
      if (
        !signatureLocationsEl.hasAttribute("data-cloned") &&
        (region.value || country.value)
      ) {
        const target = document.getElementById("signature-locations-target");
        const fragment = document.createDocumentFragment();
        signatureLocationsEl.setAttribute("data-cloned", "true");
        fragment.appendChild(signatureLocationsEl);
        target.appendChild(fragment);
      }
    }

    regionEl.options =
      countryRegions.get(country.value)?.map((r) => ({
        value: r,
        label: r,
      })) ?? [];
    countryEl.setAttribute("value", country.value);
    regionEl.setAttribute("value", region.value);

    sidebarEl.querySelector(".active")?.classList.remove("active");
    sidebarEl.querySelectorAll("a").forEach((link) => {
      if (link.textContent.trim() === region.value) {
        link.classList.add("active");
      }
    });

    if (regionEl.options.length) {
      regionEl.querySelector("button").disabled = false;
    }
  });

  // Wire up event listeners
  formEl.addEventListener("submit", (event) => event.preventDefault());
  formEl.addEventListener("change", handleFormChange);
  sidebarEl.addEventListener("click", handleListClick);
}

document.addEventListener("readystatechange", function () {
  if ([sidebarEl, listEl].every(Boolean)) {
    main();
  }
});
